import React from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {Col, Container, Row} from "@pg-design/grid-module";
import {Highlight, Text} from "@pg-design/text-module";
import {parseSearch} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {ARTICLE_TEST_IDS} from "@pg-mono/test-ids";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {IRPStore} from "../../app/rp_reducer";
import {SeoMainHeadline} from "../../atoms/SeoMainHeadline";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {ArticleCategory} from "../actions/ArticleCategory";
import {ArticleListKnowledgeSection} from "../components/article_list/ArticleListKnowledgeSection";
import {ArticleListLatestList} from "../components/article_list/ArticleListLatestList";
import {ArticleCategoriesList} from "../components/ArticleCategoriesList";
import {ArticleSearchBar} from "../components/ArticleSearchBar";
import {BlogCard} from "../components/cards/BlogCard";
import {getCategoryUrl} from "../utils/get_category_url";

const moving_in_sm = require("../assets/moving_in_sm.png");
const moving_in_md = require("../assets/moving_in_md.png");
const moving_in_lg = require("../assets/moving_in_lg.png");
const search_sm = require("../assets/search_sm.png");
const search_md = require("../assets/search_md.png");
const search_lg = require("../assets/search_lg.png");

const primaryCategory = [
    {
        picture: {sm: search_sm, md: search_md, lg: search_lg},
        url: getCategoryUrl(ArticleCategory.DuringSearch),
        title: "W trakcie poszukiwań",
        bulletPoints: ["Jak kupować mieszkanie?", "Rynek mieszkaniowy i kredyty", " Formalności związane z zakupem"],
        YTUrl: "https://www.youtube.com/playlist?list=PLpPkVRpmH_TZZq-xJh8K2ImDbdzKXWlhu"
    },
    {
        picture: {sm: moving_in_sm, md: moving_in_md, lg: moving_in_lg},
        url: getCategoryUrl(ArticleCategory.AfterPurchase),
        title: "Po zakupie mieszkania",
        bulletPoints: ["Jak mieszkać wygodnie?", "Urządzanie mieszkania", "Wykończenia wnętrz i przeprowadzki"],
        YTUrl: "https://www.youtube.com/playlist?list=PLpPkVRpmH_TZhNMn3jEBdBMmCeYgZf9vo"
    }
];

export const ArticleListView = () => {
    const location = useLocation();
    const page = parseSearch(location.search)?.page;
    const phrase = parseSearch(location.search)?.phrase;

    const list = useSelector((state: IRPStore) => state.article.list);
    const target = useResponsiveLinkTarget();

    const categoryHandler = (e: React.MouseEvent<HTMLElement>, url: string) => {
        e.stopPropagation();
        window.open(url, target);
    };
    return (
        <>
            <ArticleSearchBar />
            <Container as="article">
                <Breadcrumbs className={breadcrumbsStyle} homePageUrl={rpAppLink.base()} items={[{name: "Wiadomości", url: rpAppLink.article.list()}]} />

                {!phrase && (
                    <>
                        <section className={sectionStyle}>
                            <SeoMainHeadline
                                dataTestId={ARTICLE_TEST_IDS.HEADLINES.HOMEPAGE}
                                pageString={page && ` - strona ${page}`}
                                className={headlineStyle}
                            >
                                Dla poszukującego {page && ` - strona ${page}`}
                            </SeoMainHeadline>
                            <Text variant="headline_5" as="p">
                                Od marzenia do wprowadzenia. <Highlight>Przeprowadzimy Cię.</Highlight>
                            </Text>
                            <Text className={authorsTextStyle} variant="headline_5" as="p">
                                <a className={authorsLinkStyle} href={rpAppLink.article.author.list()}>
                                    Nasi autorzy
                                </a>{" "}
                                dokładają wszelkich starań, aby publikacje stanowiły nieocenione źródło wiedzy dla osób poszukujących nieruchomości.
                            </Text>
                            <Row>
                                {primaryCategory.map((category, idx) => (
                                    <Col className={colStyle} md={6} key={category.title} onClick={(e) => categoryHandler(e, category.url)}>
                                        <BlogCard.Body>
                                            <BlogCard.Picture
                                                loading={idx === 0 ? "eager" : "lazy"}
                                                sources={[
                                                    {minWidthPX: 0, src: category.picture.sm, width: 336, height: 274},
                                                    {minWidthPX: 336, src: category.picture.md, width: 552, height: 296},
                                                    {minWidthPX: 1168, src: category.picture.lg, width: 756, height: 296}
                                                ]}
                                                alt={category.title}
                                            />
                                            <BlogCard href={category.url}>
                                                <BlogCard.Header>{category.title}</BlogCard.Header>
                                            </BlogCard>
                                            <BlogCard.BulletList items={category.bulletPoints} />
                                            <BlogCard.Button>Zobacz artykuły</BlogCard.Button>
                                            <BlogCard.Button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                                href={category.YTUrl}
                                            >
                                                Zobacz filmy
                                            </BlogCard.Button>
                                        </BlogCard.Body>
                                    </Col>
                                ))}
                            </Row>
                        </section>
                        <ArticleListKnowledgeSection className={knowledgeSectionStyle} handleCategoryClick={categoryHandler} />
                    </>
                )}
                <ArticleListLatestList phrase={phrase} showHeader articles={list.articles} count={list.count} location={location} />
                <ArticleCategoriesList />
            </Container>
        </>
    );
};

const breadcrumbsStyle = css({
    marginBottom: 3
});

const sectionStyle = css({
    marginBottom: 5,
    md: {
        marginBottom: 10
    }
});

const headlineStyle = css({
    marginBottom: 1
});

const authorsTextStyle = css({
    marginBottom: 3
});

const authorsLinkStyle = css({
    textDecoration: "underline"
});

const colStyle = css({
    margin: 0,
    marginBottom: 2,
    md: {
        margin: 0
    }
});

const knowledgeSectionStyle = css({
    margin: 0,
    marginBottom: 6,
    md: {
        marginBottom: 5
    }
});
import React from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {Col, Container, Row} from "@pg-design/grid-module";
import {Highlight, Text} from "@pg-design/text-module";
import {parseSearch} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {ARTICLE_TEST_IDS} from "@pg-mono/test-ids";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {IRPStore} from "../../app/rp_reducer";
import {SeoMainHeadline} from "../../atoms/SeoMainHeadline";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {ArticleCategory} from "../actions/ArticleCategory";
import {ArticleListKnowledgeSection} from "../components/article_list/ArticleListKnowledgeSection";
import {ArticleListLatestList} from "../components/article_list/ArticleListLatestList";
import {ArticleCategoriesList} from "../components/ArticleCategoriesList";
import {ArticleSearchBar} from "../components/ArticleSearchBar";
import {BlogCard} from "../components/cards/BlogCard";
import {getCategoryUrl} from "../utils/get_category_url";

const moving_in_sm = require("../assets/moving_in_sm.png");
const moving_in_md = require("../assets/moving_in_md.png");
const moving_in_lg = require("../assets/moving_in_lg.png");
const search_sm = require("../assets/search_sm.png");
const search_md = require("../assets/search_md.png");
const search_lg = require("../assets/search_lg.png");

const primaryCategory = [
    {
        picture: {sm: search_sm, md: search_md, lg: search_lg},
        url: getCategoryUrl(ArticleCategory.DuringSearch),
        title: "W trakcie poszukiwań",
        bulletPoints: ["Jak kupować mieszkanie?", "Rynek mieszkaniowy i kredyty", " Formalności związane z zakupem"],
        YTUrl: "https://www.youtube.com/playlist?list=PLpPkVRpmH_TZZq-xJh8K2ImDbdzKXWlhu"
    },
    {
        picture: {sm: moving_in_sm, md: moving_in_md, lg: moving_in_lg},
        url: getCategoryUrl(ArticleCategory.AfterPurchase),
        title: "Po zakupie mieszkania",
        bulletPoints: ["Jak mieszkać wygodnie?", "Urządzanie mieszkania", "Wykończenia wnętrz i przeprowadzki"],
        YTUrl: "https://www.youtube.com/playlist?list=PLpPkVRpmH_TZhNMn3jEBdBMmCeYgZf9vo"
    }
];

export const ArticleListView = () => {
    const location = useLocation();
    const page = parseSearch(location.search)?.page;
    const phrase = parseSearch(location.search)?.phrase;

    const list = useSelector((state: IRPStore) => state.article.list);
    const target = useResponsiveLinkTarget();

    const categoryHandler = (e: React.MouseEvent<HTMLElement>, url: string) => {
        e.stopPropagation();
        window.open(url, target);
    };
    return (
        <>
            <ArticleSearchBar />
            <Container as="article">
                <Breadcrumbs className={breadcrumbsStyle} homePageUrl={rpAppLink.base()} items={[{name: "Wiadomości", url: rpAppLink.article.list()}]} />

                {!phrase && (
                    <>
                        <section className={sectionStyle}>
                            <SeoMainHeadline
                                dataTestId={ARTICLE_TEST_IDS.HEADLINES.HOMEPAGE}
                                pageString={page && ` - strona ${page}`}
                                className={headlineStyle}
                            >
                                Dla poszukującego {page && ` - strona ${page}`}
                            </SeoMainHeadline>
                            <Text variant="headline_5" as="p">
                                Od marzenia do wprowadzenia. <Highlight>Przeprowadzimy Cię.</Highlight>
                            </Text>
                            <Text className={authorsTextStyle} variant="headline_5" as="p">
                                <a className={authorsLinkStyle} href={rpAppLink.article.author.list()}>
                                    Nasi autorzy
                                </a>{" "}
                                dokładają wszelkich starań, aby publikacje stanowiły nieocenione źródło wiedzy dla osób poszukujących nieruchomości.
                            </Text>
                            <Row>
                                {primaryCategory.map((category, idx) => (
                                    <Col className={colStyle} md={6} key={category.title} onClick={(e) => categoryHandler(e, category.url)}>
                                        <BlogCard.Body>
                                            <BlogCard.Picture
                                                loading={idx === 0 ? "eager" : "lazy"}
                                                sources={[
                                                    {minWidthPX: 0, src: category.picture.sm, width: 336, height: 274},
                                                    {minWidthPX: 336, src: category.picture.md, width: 552, height: 296},
                                                    {minWidthPX: 1168, src: category.picture.lg, width: 756, height: 296}
                                                ]}
                                                alt={category.title}
                                            />
                                            <BlogCard href={category.url}>
                                                <BlogCard.Header>{category.title}</BlogCard.Header>
                                            </BlogCard>
                                            <BlogCard.BulletList items={category.bulletPoints} />
                                            <BlogCard.Button>Zobacz artykuły</BlogCard.Button>
                                            <BlogCard.Button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                                href={category.YTUrl}
                                            >
                                                Zobacz filmy
                                            </BlogCard.Button>
                                        </BlogCard.Body>
                                    </Col>
                                ))}
                            </Row>
                        </section>
                        <ArticleListKnowledgeSection className={knowledgeSectionStyle} handleCategoryClick={categoryHandler} />
                    </>
                )}
                <ArticleListLatestList phrase={phrase} showHeader articles={list.articles} count={list.count} location={location} />
                <ArticleCategoriesList />
            </Container>
        </>
    );
};

const breadcrumbsStyle = css({
    marginBottom: 3
});

const sectionStyle = css({
    marginBottom: 5,
    md: {
        marginBottom: 10
    }
});

const headlineStyle = css({
    marginBottom: 1
});

const authorsTextStyle = css({
    marginBottom: 3
});

const authorsLinkStyle = css({
    textDecoration: "underline"
});

const colStyle = css({
    margin: 0,
    marginBottom: 2,
    md: {
        margin: 0
    }
});

const knowledgeSectionStyle = css({
    margin: 0,
    marginBottom: 6,
    md: {
        marginBottom: 5
    }
});
